export const DEFAULT_MD_TEXT_CHAR_THRESHOLD = 200_000
export const DEFAULT_MD_TEXT_LINE_THRESHOLD = 2_000

export interface MdDocumentThresholds {
  charThreshold?: number
  lineThreshold?: number
}

export interface MdDocumentSection {
  id: string
  level: number
  title: string
  startOffset: number
  endOffset: number
  startLine: number
  endLine: number
  body: string
}

export interface MdDocumentSummary {
  characterCount: number
  lineCount: number
  sectionCount: number
}

export interface MdSearchMatch {
  startOffset: number
  endOffset: number
  line: number
  sectionId: string
}

export interface MdDocumentModelOptions extends MdDocumentThresholds {
  forceLarge?: boolean
}

export interface MdDocumentModel {
  source: string
  isEmpty: boolean
  isLarge: boolean
  sections: MdDocumentSection[]
  summary: MdDocumentSummary
}

export function slugifyHeadingTitle(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  return slug || 'section'
}

export function lineNumberAtOffset(source: string, offset: number): number {
  if (offset <= 0) {
    return 1
  }

  let line = 1
  for (let index = 0; index < offset && index < source.length; index += 1) {
    if (source[index] === '\n') {
      line += 1
    }
  }

  return line
}

export function countLines(source: string): number {
  if (source.length === 0) {
    return 0
  }

  return source.split('\n').length
}

export function summarizeMarkdownDocument(source: string): MdDocumentSummary {
  return {
    characterCount: source.length,
    lineCount: countLines(source),
    sectionCount: 0,
  }
}

export function isLargeMarkdownDocument(source: string, options?: MdDocumentModelOptions): boolean {
  if (source.trim() === '') {
    return false
  }

  if (options?.forceLarge) {
    return true
  }

  const charThreshold = options?.charThreshold ?? DEFAULT_MD_TEXT_CHAR_THRESHOLD
  const lineThreshold = options?.lineThreshold ?? DEFAULT_MD_TEXT_LINE_THRESHOLD

  if (source.length > charThreshold) {
    return true
  }

  return countLines(source) > lineThreshold
}

export function isLargeMarkdownSection(sectionBody: string, options?: MdDocumentThresholds): boolean {
  return isLargeMarkdownDocument(sectionBody, { forceLarge: false, ...options })
}

function createSectionId(slug: string, slugCounts: Map<string, number>): string {
  const occurrence = slugCounts.get(slug) ?? 0
  slugCounts.set(slug, occurrence + 1)
  return `${slug}-${occurrence}`
}

export function parseMarkdownSections(source: string): MdDocumentSection[] {
  if (source.length === 0) {
    return []
  }

  const lines = source.split('\n')
  const headingStarts: { level: number; title: string; offset: number; line: number }[] = []
  let offset = 0
  let inFence = false

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex]
    const lineStartOffset = offset

    if (/^```/.test(line)) {
      inFence = !inFence
    } else if (!inFence) {
      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)
      if (headingMatch) {
        headingStarts.push({
          level: headingMatch[1].length,
          title: headingMatch[2].trim(),
          offset: lineStartOffset,
          line: lineIndex + 1,
        })
      }
    }

    offset += line.length
    if (lineIndex < lines.length - 1) {
      offset += 1
    }
  }

  const slugCounts = new Map<string, number>()
  const sections: MdDocumentSection[] = []

  if (headingStarts.length === 0) {
    const endOffset = source.length
    sections.push({
      id: createSectionId('document', slugCounts),
      level: 0,
      title: 'Document',
      startOffset: 0,
      endOffset,
      startLine: 1,
      endLine: lineNumberAtOffset(source, Math.max(0, endOffset - 1)),
      body: source,
    })
    return sections
  }

  if (headingStarts[0].offset > 0) {
    const endOffset = headingStarts[0].offset
    sections.push({
      id: createSectionId('preamble', slugCounts),
      level: 0,
      title: 'Preamble',
      startOffset: 0,
      endOffset,
      startLine: 1,
      endLine: headingStarts[0].line - 1 > 0 ? headingStarts[0].line - 1 : 1,
      body: source.slice(0, endOffset),
    })
  }

  headingStarts.forEach((heading, index) => {
    const startOffset = heading.offset
    const endOffset = index + 1 < headingStarts.length ? headingStarts[index + 1].offset : source.length
    const slug = slugifyHeadingTitle(heading.title)

    sections.push({
      id: createSectionId(slug, slugCounts),
      level: heading.level,
      title: heading.title,
      startOffset,
      endOffset,
      startLine: heading.line,
      endLine:
        index + 1 < headingStarts.length
          ? headingStarts[index + 1].line - 1
          : lineNumberAtOffset(source, Math.max(0, endOffset - 1)),
      body: source.slice(startOffset, endOffset),
    })
  })

  return sections
}

export function buildMdDocumentModel(source: string, options?: MdDocumentModelOptions): MdDocumentModel {
  const sections = parseMarkdownSections(source)
  const summary = summarizeMarkdownDocument(source)
  summary.sectionCount = sections.length

  return {
    source,
    isEmpty: source.trim() === '',
    isLarge: isLargeMarkdownDocument(source, options),
    sections,
    summary,
  }
}

export function getSectionById(sections: MdDocumentSection[], sectionId: string): MdDocumentSection | undefined {
  return sections.find((section) => section.id === sectionId)
}

export function getDefaultSectionId(sections: MdDocumentSection[]): string | undefined {
  return sections[0]?.id
}

export function findSectionIdForOffset(sections: MdDocumentSection[], offset: number): string {
  if (sections.length === 0) {
    return ''
  }

  let low = 0
  let high = sections.length - 1

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const section = sections[mid]
    if (offset < section.startOffset) {
      high = mid - 1
    } else if (offset >= section.endOffset) {
      low = mid + 1
    } else {
      return section.id
    }
  }

  return sections[sections.length - 1]?.id ?? ''
}

function indexOfInsensitive(source: string, query: string, fromIndex: number): number {
  const queryLength = query.length
  if (queryLength === 0) {
    return fromIndex
  }

  const firstQueryCharLower = query[0].toLowerCase()
  const firstQueryCharUpper = query[0].toUpperCase()
  const lastIndex = source.length - queryLength

  for (let index = fromIndex; index <= lastIndex; index += 1) {
    const sourceChar = source[index]
    if (
      sourceChar !== firstQueryCharLower &&
      sourceChar !== firstQueryCharUpper &&
      sourceChar.toLowerCase() !== firstQueryCharLower
    ) {
      continue
    }

    let matched = true
    for (let offset = 1; offset < queryLength; offset += 1) {
      if (source[index + offset].toLowerCase() !== query[offset].toLowerCase()) {
        matched = false
        break
      }
    }

    if (matched) {
      return index
    }
  }

  return -1
}

export const DEFAULT_MD_TEXT_SEARCH_MATCH_LIMIT = 2_000

export function indexMarkdownSearchMatches(
  source: string,
  sections: MdDocumentSection[],
  searchValue: string,
  maxMatches: number = DEFAULT_MD_TEXT_SEARCH_MATCH_LIMIT
): MdSearchMatch[] {
  const query = searchValue.trim()
  if (query === '' || maxMatches <= 0) {
    return []
  }

  const matches: MdSearchMatch[] = []
  let searchFrom = 0
  let line = 1
  let scanOffset = 0

  while (searchFrom < source.length && matches.length < maxMatches) {
    const found = indexOfInsensitive(source, query, searchFrom)
    if (found === -1) {
      break
    }

    while (scanOffset < found) {
      if (source[scanOffset] === '\n') {
        line += 1
      }
      scanOffset += 1
    }

    matches.push({
      startOffset: found,
      endOffset: found + query.length,
      line,
      sectionId: findSectionIdForOffset(sections, found),
    })

    searchFrom = found + 1
  }

  return matches
}

export function spliceSectionBody(
  source: string,
  sections: MdDocumentSection[],
  sectionId: string,
  newBody: string
): string {
  const section = getSectionById(sections, sectionId)
  if (!section) {
    return source
  }

  return `${source.slice(0, section.startOffset)}${newBody}${source.slice(section.endOffset)}`
}

export function getActiveMatchWithinSection(
  section: MdDocumentSection,
  match: MdSearchMatch | undefined
): { start: number; end: number } | undefined {
  if (!match) {
    return undefined
  }

  if (match.startOffset < section.startOffset || match.endOffset > section.endOffset) {
    return undefined
  }

  return {
    start: match.startOffset - section.startOffset,
    end: match.endOffset - section.startOffset,
  }
}

export function highlightActiveMatchInText(
  text: string,
  activeRange: { start: number; end: number } | undefined
): { before: string; match: string; after: string } {
  if (!activeRange || activeRange.start < 0 || activeRange.end > text.length || activeRange.start >= activeRange.end) {
    return { before: text, match: '', after: '' }
  }

  return {
    before: text.slice(0, activeRange.start),
    match: text.slice(activeRange.start, activeRange.end),
    after: text.slice(activeRange.end),
  }
}
